import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ExternalLink, Apple, Play, Scale } from "lucide-react";
import { api } from "../lib/api";
import Reveal, { Label } from "../components/Reveal";
import { GALLERY_FILTERS } from "../lib/constants";
import Seo from "../components/Seo";
import { useLang } from "../context/LanguageContext";

const slug = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export default function Gallery() {
  const [apps, setApps] = useState([]);
  const [filter, setFilter] = useState("Tous");
  const { t } = useLang();

  useEffect(() => {
    api.get("/public/gallery").then((r) => setApps(r.data)).catch(() => {});
  }, []);

  const matches = (a) => {
    if (filter === "Tous") return true;
    const map = { Mobile: ["Mobile"], Web: ["Web"], Social: ["Réseaux sociaux", "Social"], Santé: ["Santé", "Santé et bien-être"] };
    const keys = map[filter] || [filter];
    return keys.some((k) => (a.category || "").includes(k) || (a.type || "").includes(k));
  };
  const list = apps.filter(matches);

  return (
    <div>
      <Seo title={t("gl_label")} description={t("gl_h1a") + " " + t("gl_h1b")} path="/applications" />
      <section className="mx-auto max-w-[1400px] px-5 pt-36 pb-12 sm:px-8">
        <Reveal><Label>{t("gl_label")}</Label></Reveal>
        <Reveal delay={0.05}>
          <h1 className="mt-6 max-w-4xl font-display text-4xl font-900 leading-[0.95] tracking-tighter text-white sm:text-6xl">
            {t("gl_h1a")}<br /><span className="text-cyan text-glow">{t("gl_h1b")}</span>
          </h1>
        </Reveal>
      </section>

      <section className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="flex flex-wrap gap-2 border-y border-white/10 py-5">
          {GALLERY_FILTERS.map((f) => (
            <button
              key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`}
              className={`border px-4 py-2 text-xs uppercase tracking-wider transition-all hover:-translate-y-0.5 ${
                filter === f ? "border-cyan bg-cyan text-void" : "border-white/15 text-ink-secondary hover:text-white"
              }`}
            >
              {t(`glf_${slug(f)}`)}
            </button>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-5 py-16 sm:px-8">
        <AnimatePresence mode="popLayout">
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {list.map((a, i) => (
              <motion.div
                key={a.id} layout
                initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.06 }}
                data-testid={`realized-app-${i}`}
              >
                <div className="group border border-white/10 bg-surface transition-colors hover:border-cyan/40">
                  <div className="relative aspect-[9/12] overflow-hidden">
                    <div className="absolute inset-0 z-10 bg-gradient-to-t from-void via-void/10 to-transparent" />
                    <img src={a.image} alt={a.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    {a.is_winner_of_week && (
                      <div className="absolute left-4 top-4 z-20 border border-cyan bg-void/80 px-2.5 py-1 text-[10px] uppercase tracking-widest text-cyan">🏆 {t("gl_winner")}</div>
                    )}
                    {a.committee_recommended && (
                      <div className="absolute right-4 top-4 z-20 flex items-center gap-1 border border-amber-400 bg-void/80 px-2.5 py-1 text-[10px] uppercase tracking-widest text-amber-400" data-testid={`gallery-committee-reco-${i}`}>
                        <Scale size={11} /> Recommandée par le Comité
                      </div>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 z-20 p-5">
                      <div className="text-[11px] uppercase tracking-widest text-cyan">{a.category}</div>
                      <div className="mt-1 font-display text-2xl font-700 text-white">{a.name}</div>
                      <div className="text-xs text-ink-secondary">{t("gl_by")} {a.creator} · {a.country}</div>
                    </div>
                  </div>
                  <div className="p-5">
                    <p className="text-sm leading-relaxed text-ink-secondary">{a.description}</p>
                    <div className="mt-4 flex items-center gap-3 text-ink-tertiary">
                      <a href={a.website} className="transition-colors hover:text-cyan" aria-label="site"><ExternalLink size={16} /></a>
                      <a href={a.app_store} className="transition-colors hover:text-cyan" aria-label="app store"><Apple size={16} /></a>
                      <a href={a.play_store} className="transition-colors hover:text-cyan" aria-label="play store"><Play size={16} /></a>
                      <span className="ml-auto text-[11px] text-ink-tertiary">{t("gl_launched")} {a.launched_at}</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </AnimatePresence>
        {list.length === 0 && <p className="py-20 text-center text-ink-secondary">{t("gl_empty")}</p>}
      </section>
    </div>
  );
}
